// RouteNetworkPanel.jsx - Interactive Route Health Visualization
// "Ping Network" - shows all routes radiating from hub with health colors

import React, { useState, useMemo } from 'react';
import { useWorldState } from '../state/WorldStateContext';
import { debug } from '../../lib/debug.js';

// ============================================
// AIRPORT COORDINATES (simplified for visualization)
// ============================================

const AIRPORT_COORDS = {
  // PNW
  KPDX: { x: 120, y: 180, name: 'Portland' },
  KSEA: { x: 115, y: 140, name: 'Seattle' },
  KGEG: { x: 160, y: 130, name: 'Spokane' },
  KEUG: { x: 110, y: 210, name: 'Eugene' },
  KMFR: { x: 100, y: 240, name: 'Medford' },
  KBOI: { x: 180, y: 190, name: 'Boise' },
  KRDM: { x: 140, y: 200, name: 'Redmond' },
  
  // Mountain
  KBZN: { x: 220, y: 160, name: 'Bozeman' },
  KJAC: { x: 230, y: 200, name: 'Jackson' },
  KIDA: { x: 200, y: 210, name: 'Idaho Falls' },
  KSLC: { x: 220, y: 240, name: 'Salt Lake' },
  KRNO: { x: 130, y: 280, name: 'Reno' },
  KDEN: { x: 280, y: 240, name: 'Denver' },
  KCOS: { x: 290, y: 270, name: 'Colorado Springs' },
  KASE: { x: 260, y: 250, name: 'Aspen' },
  
  // California
  KSFO: { x: 80, y: 290, name: 'San Francisco' },
  KOAK: { x: 85, y: 295, name: 'Oakland' },
  KSJC: { x: 90, y: 310, name: 'San Jose' },
  KSMF: { x: 100, y: 280, name: 'Sacramento' },
  KLAX: { x: 100, y: 380, name: 'Los Angeles' },
  KSAN: { x: 120, y: 410, name: 'San Diego' },
  
  // Southwest
  KPHX: { x: 180, y: 380, name: 'Phoenix' },
  KLAS: { x: 150, y: 340, name: 'Las Vegas' },
  
  // Midwest
  KORD: { x: 420, y: 200, name: 'Chicago' },
  KMSP: { x: 380, y: 160, name: 'Minneapolis' },
  KDTW: { x: 460, y: 200, name: 'Detroit' },
  
  // South
  KATL: { x: 480, y: 320, name: 'Atlanta' },
  KBNA: { x: 450, y: 290, name: 'Nashville' },
  KCLT: { x: 510, y: 300, name: 'Charlotte' },
  
  // Northeast
  KEWR: { x: 540, y: 200, name: 'Newark' },
  
  // Alaska
  PANC: { x: 60, y: 60, name: 'Anchorage' },
  PAFA: { x: 80, y: 40, name: 'Fairbanks' },
  PADQ: { x: 40, y: 80, name: 'Kodiak' },
};

// ============================================
// ROUTE NETWORK PANEL
// ============================================

export function RouteNetworkPanel({ onClose, onSelectRoute }) {
  const { worldState } = useWorldState();
  const [selectedRoute, setSelectedRoute] = useState(null);
  const [viewMode, setViewMode] = useState('health'); // health, revenue, frequency

  // slots is an object like { KPDX: [...], KSEA: [...] } - convert to list of airport codes
  const slotsObj = worldState?.slots || {};
  const slotAirports = Object.keys(slotsObj).filter(code => slotsObj[code]?.length > 0);

  // Debug logging
  debug('[RouteNetworkPanel] Data check:', {
    flightsCount: worldState?.flights?.length || 0,
    slotsAirports: slotAirports,
    hub: worldState?.airline?.hub || slotAirports[0] || 'KPDX',
  });

  // Get hub (first slot airport or airline hub)
  const hub = worldState?.airline?.hub || slotAirports[0] || 'KPDX';
  const hubCoords = AIRPORT_COORDS[hub] || { x: 300, y: 250 };

  // Build route data from flights
  const routes = useMemo(() => {
    return buildRouteData(worldState, slotAirports, hub);
  }, [worldState, slotAirports, hub]);

  return (
    <div style={styles.overlay}>
      <div style={styles.panel}>
        
        {/* Header */}
        <div style={styles.header}>
          <div style={styles.headerTitle}>
            <span style={styles.pingDot} />
            ROUTE NETWORK
          </div>
          <button style={styles.closeButton} onClick={onClose}>X</button>
        </div>

        {/* View Mode Tabs */}
        <div style={styles.viewTabs}>
          {['health', 'revenue', 'frequency'].map(mode => (
            <button
              key={mode}
              style={{
                ...styles.viewTab,
                ...(viewMode === mode ? styles.viewTabActive : {}),
              }}
              onClick={() => setViewMode(mode)}
            >
              {mode.toUpperCase()}
            </button>
          ))}
        </div>

        {/* Map Visualization */}
        <div style={styles.mapContainer}>
          <svg viewBox="0 0 600 450" style={styles.map}>
            
            {/* Background grid */}
            <defs>
              <pattern id="grid" width="20" height="20" patternUnits="userSpaceOnUse">
                <path d="M 20 0 L 0 0 0 20" fill="none" stroke="#1a1a1a" strokeWidth="0.5"/>
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#grid)" />

            {/* Route lines */}
            {routes.map(route => (
              <RouteLine
                key={`${route.origin}-${route.destination}`}
                route={route}
                hubCoords={hubCoords}
                viewMode={viewMode}
                isSelected={selectedRoute?.destination === route.destination}
                onClick={() => setSelectedRoute(route)}
              />
            ))}

            {/* Airport dots for slots we have */}
            {slotAirports.map(code => {
              const coords = AIRPORT_COORDS[code];
              if (!coords) return null;
              const isHub = code === hub;
              
              return (
                <g key={code}>
                  {/* Pulse effect for hub */}
                  {isHub && (
                    <circle
                      cx={coords.x}
                      cy={coords.y}
                      r="12"
                      fill="none"
                      stroke="#00ff88"
                      strokeWidth="1"
                      opacity="0.3"
                      style={{ animation: 'pulse 2s infinite' }}
                    />
                  )}
                  
                  {/* Airport dot */}
                  <circle
                    cx={coords.x}
                    cy={coords.y}
                    r={isHub ? 8 : 5}
                    fill={isHub ? '#00ff88' : '#444'}
                    stroke={isHub ? '#00ff88' : '#666'}
                    strokeWidth="2"
                  />
                  
                  {/* Airport label */}
                  <text
                    x={coords.x}
                    y={coords.y + (isHub ? 22 : 18)}
                    fill="#666"
                    fontSize="9"
                    textAnchor="middle"
                    fontFamily="monospace"
                  >
                    {code.replace('K', '')}
                  </text>
                </g>
              );
            })}

          </svg>
        </div>

        {/* Selected Route Detail */}
        {selectedRoute && (
          <RouteDetail 
            route={selectedRoute} 
            onAbandon={() => handleAbandonRoute(selectedRoute)}
            onAdjust={() => handleAdjustRoute(selectedRoute)}
          />
        )}

        {/* Legend */}
        <div style={styles.legend}>
          <div style={styles.legendItem}>
            <div style={{ ...styles.legendDot, backgroundColor: '#00ff88' }} />
            <span>Healthy (90%+ OTP)</span>
          </div>
          <div style={styles.legendItem}>
            <div style={{ ...styles.legendDot, backgroundColor: '#88ff00' }} />
            <span>Good (75-90%)</span>
          </div>
          <div style={styles.legendItem}>
            <div style={{ ...styles.legendDot, backgroundColor: '#ffcc00' }} />
            <span>Warning (50-75%)</span>
          </div>
          <div style={styles.legendItem}>
            <div style={{ ...styles.legendDot, backgroundColor: '#ff6600' }} />
            <span>Struggling (25-50%)</span>
          </div>
          <div style={styles.legendItem}>
            <div style={{ ...styles.legendDot, backgroundColor: '#ff0000' }} />
            <span>Critical (&lt;25%)</span>
          </div>
        </div>

      </div>
    </div>
  );
}

// ============================================
// ROUTE LINE COMPONENT
// ============================================

function RouteLine({ route, hubCoords, viewMode, isSelected, onClick }) {
  const destCoords = AIRPORT_COORDS[route.destination];
  if (!destCoords) return null;

  const color = getRouteColor(route, viewMode);
  const thickness = getRouteThickness(route);

  return (
    <g onClick={onClick} style={{ cursor: 'pointer' }}>
      {/* Glow effect for selected */}
      {isSelected && (
        <line
          x1={hubCoords.x}
          y1={hubCoords.y}
          x2={destCoords.x}
          y2={destCoords.y}
          stroke={color}
          strokeWidth={thickness + 4}
          opacity="0.3"
        />
      )}
      
      {/* Main line */}
      <line
        x1={hubCoords.x}
        y1={hubCoords.y}
        x2={destCoords.x}
        y2={destCoords.y}
        stroke={color}
        strokeWidth={thickness}
        strokeLinecap="round"
        opacity={isSelected ? 1 : 0.7}
      />

      {/* Destination dot */}
      <circle
        cx={destCoords.x}
        cy={destCoords.y}
        r="4"
        fill={color}
      />
    </g>
  );
}

// ============================================
// ROUTE DETAIL PANEL
// ============================================

function RouteDetail({ route, onAbandon, onAdjust }) {
  return (
    <div style={styles.routeDetail}>
      <div style={styles.routeHeader}>
        <span style={styles.routeCode}>{route.origin} - {route.destination}</span>
        <span style={styles.routeHealth}>{Math.round(route.health * 100)}% OTP</span>
      </div>
      
      <div style={styles.routeStats}>
        <div style={styles.routeStat}>
          <span style={styles.statLabel}>Revenue/mo</span>
          <span style={styles.statValue}>${formatMoney(route.monthlyRevenue)}</span>
        </div>
        <div style={styles.routeStat}>
          <span style={styles.statLabel}>Load Factor</span>
          <span style={styles.statValue}>{Math.round(route.loadFactor * 100)}%</span>
        </div>
        <div style={styles.routeStat}>
          <span style={styles.statLabel}>Frequency</span>
          <span style={styles.statValue}>{route.frequency}x/day</span>
        </div>
        <div style={styles.routeStat}>
          <span style={styles.statLabel}>Flights</span>
          <span style={styles.statValue}>{route.totalFlights}</span>
        </div>
      </div>

      <div style={styles.routeActions}>
        <button style={styles.routeButton} onClick={onAdjust}>
          Adjust Frequency
        </button>
        <button style={{ ...styles.routeButton, ...styles.routeButtonDanger }} onClick={onAbandon}>
          Abandon Route
        </button>
      </div>
    </div>
  );
}

// ============================================
// HELPER FUNCTIONS
// ============================================

function buildRouteData(worldState, slotAirports, hub) {
  const flights = worldState?.flights || [];
  const routeMap = new Map();

  debug('[buildRouteData] Input:', {
    flightsCount: flights.length,
    slotAirports,
    hub,
    sampleFlight: flights[0] ? { origin: flights[0].origin, dest: flights[0].destination, phase: flights[0].phase } : null
  });

  // Group flights by route
  flights.forEach(f => {
    // Only routes from/to hub
    if (f.origin !== hub && f.destination !== hub) return;

    const dest = f.origin === hub ? f.destination : f.origin;
    const key = `${hub}-${dest}`;

    if (!routeMap.has(key)) {
      routeMap.set(key, {
        origin: hub,
        destination: dest,
        flights: [],
        health: 1.0,
        monthlyRevenue: 0,
        loadFactor: 0,
        frequency: 0,
        totalFlights: 0,
      });
    }
    routeMap.get(key).flights.push(f);
  });

  // Calculate metrics
  routeMap.forEach(route => {
    // Include all flights, not just completed (to show active routes)
    route.totalFlights = route.flights.length;
    const completed = route.flights.filter(f => f.phase === 'completed' || f.status === 'completed');

    if (completed.length > 0) {
      // Health = on-time rate (delay < 15 min counts as on-time)
      const onTime = completed.filter(f => (f.totalDelayMinutes || 0) < 15).length;
      route.health = onTime / completed.length;

      // Revenue
      route.monthlyRevenue = completed.reduce((sum, f) => sum + (f.revenue || f.profit || 0), 0);

      // Load factor
      const totalPax = completed.reduce((sum, f) => sum + (f.passengers_booked || f.passengers || 0), 0);
      const totalCapacity = completed.reduce((sum, f) => sum + (f.capacity || 150), 0);
      route.loadFactor = totalCapacity > 0 ? totalPax / totalCapacity : 0.8;

      // Frequency (flights per day, rough estimate)
      route.frequency = Math.max(1, Math.round(route.totalFlights / 7));
    } else if (route.totalFlights > 0) {
      // Has flights but none completed yet - show as healthy pending
      route.health = 1.0;
      route.loadFactor = 0.8;
      route.frequency = Math.max(1, Math.round(route.totalFlights / 7));
    }
  });

  debug('[buildRouteData] Output routes:', routeMap.size);
  return Array.from(routeMap.values());
}

function getRouteColor(route, viewMode) {
  if (viewMode === 'health') {
    const h = route.health;
    if (h >= 0.9) return '#00ff88';
    if (h >= 0.75) return '#88ff00';
    if (h >= 0.5) return '#ffcc00';
    if (h >= 0.25) return '#ff6600';
    return '#ff0000';
  }
  
  if (viewMode === 'revenue') {
    const r = route.monthlyRevenue;
    if (r >= 500000) return '#00ff88';
    if (r >= 200000) return '#88ff00';
    if (r >= 0) return '#ffcc00';
    return '#ff0000';
  }
  
  // frequency
  const f = route.frequency;
  if (f >= 4) return '#00ff88';
  if (f >= 2) return '#88ff00';
  if (f >= 1) return '#ffcc00';
  return '#ff6600';
}

function getRouteThickness(route) {
  // Thicker = more flights
  const f = route.frequency;
  if (f >= 4) return 4;
  if (f >= 2) return 3;
  return 2;
}

function formatMoney(cents) {
  if (!cents) return '0';
  if (Math.abs(cents) >= 100000000) {
    return `${(cents / 100000000).toFixed(1)}M`;
  } else if (Math.abs(cents) >= 100000) {
    return `${(cents / 100000).toFixed(0)}K`;
  }
  return cents.toLocaleString();
}

// ============================================
// STYLES
// ============================================

const styles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.9)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },

  panel: {
    width: '900px',
    maxHeight: '90vh',
    backgroundColor: '#0a0a0a',
    border: '1px solid #222',
    borderRadius: '8px',
    display: 'flex',
    flexDirection: 'column',
    fontFamily: 'inherit',
  },

  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 20px',
    borderBottom: '1px solid #222',
  },

  headerTitle: {
    fontSize: '13px',
    fontWeight: '600',
    letterSpacing: '2px',
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },

  pingDot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    backgroundColor: '#00ff88',
    animation: 'pulse 1.5s infinite',
  },

  closeButton: {
    background: 'none',
    border: 'none',
    color: '#666',
    fontSize: '24px',
    cursor: 'pointer',
    padding: '0 8px',
  },

  viewTabs: {
    display: 'flex',
    gap: '4px',
    padding: '12px 20px',
    borderBottom: '1px solid #1a1a1a',
  },

  viewTab: {
    background: 'none',
    border: '1px solid #333',
    borderRadius: '3px',
    padding: '6px 16px',
    fontSize: '10px',
    fontWeight: '500',
    letterSpacing: '1px',
    color: '#666',
    cursor: 'pointer',
    fontFamily: 'inherit',
  },

  viewTabActive: {
    borderColor: '#00ff88',
    color: '#00ff88',
  },

  mapContainer: {
    flex: 1,
    minHeight: '400px',
    padding: '20px',
    backgroundColor: '#050505',
  },

  map: {
    width: '100%',
    height: '100%',
  },

  legend: {
    display: 'flex',
    gap: '20px',
    padding: '12px 20px',
    borderTop: '1px solid #1a1a1a',
    justifyContent: 'center',
  },

  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '10px',
    color: '#666',
  },

  legendDot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
  },

  routeDetail: {
    padding: '16px 20px',
    borderTop: '1px solid #222',
    backgroundColor: '#111',
  },

  routeHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '12px',
  },

  routeCode: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#fff',
    letterSpacing: '1px',
  },

  routeHealth: {
    fontSize: '12px',
    color: '#00ff88',
    fontWeight: '500',
  },

  routeStats: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '16px',
    marginBottom: '16px',
  },

  routeStat: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },

  statLabel: {
    fontSize: '9px',
    color: '#666',
    textTransform: 'uppercase',
    letterSpacing: '1px',
  },

  statValue: {
    fontSize: '14px',
    color: '#fff',
    fontWeight: '500',
  },

  routeActions: {
    display: 'flex',
    gap: '8px',
    justifyContent: 'flex-end',
  },

  routeButton: {
    background: 'none',
    border: '1px solid #333',
    borderRadius: '3px',
    padding: '8px 16px',
    fontSize: '11px',
    color: '#888',
    cursor: 'pointer',
    fontFamily: 'inherit',
  },

  routeButtonDanger: {
    borderColor: '#661111',
    color: '#ff4444',
  },
};

// Add CSS animation for pulse
const styleSheet = document.createElement('style');
styleSheet.textContent = `
  @keyframes pulse {
    0% { opacity: 0.3; transform: scale(1); }
    50% { opacity: 0.1; transform: scale(1.5); }
    100% { opacity: 0.3; transform: scale(1); }
  }
`;
document.head.appendChild(styleSheet);

export default RouteNetworkPanel;
